import { DragHandle } from "@tiptap/extension-drag-handle-react";
import { ActionIcon, Menu } from "@mantine/core";
import {
  IconGripVertical,
  IconPlus,
  IconTrash,
  IconCopy,
} from "@tabler/icons-react";
import type { Editor } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface DragHandleMenuProps {
  editor: Editor | null;
}

export default function DragHandleMenu({ editor }: DragHandleMenuProps) {
  const { t } = useTranslation();
  const [menuOpened, setMenuOpened] = useState(false);
  // Track currently hovered node + its absolute position. Updated only while
  // the menu is closed, so interacting with the menu does not cause the
  // drag-handle to jump to a different block.
  const hoveredPosRef = useRef<number | null>(null);
  const hoveredNodeNameRef = useRef<string | null>(null);

  const handleNodeChange = useCallback(
    ({ node, pos }: { node: any; pos: number }) => {
      if (menuOpened) return; // freeze target while menu is open
      hoveredPosRef.current = typeof pos === "number" ? pos : null;
      hoveredNodeNameRef.current = node?.type?.name ?? null;
    },
    [menuOpened],
  );

  if (!editor) return null;

  // Resolve the position to operate on. Prefer the position captured via
  // `onNodeChange` (what the drag-handle is pointing at); fall back to the
  // current selection's block.
  const getTargetPos = (): number | null => {
    const pos = hoveredPosRef.current;
    if (typeof pos === "number" && pos >= 0 && pos <= editor.state.doc.content.size) {
      return pos;
    }
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d--) {
      return $from.before(d);
    }
    return null;
  };

  const selectTargetNode = () => {
    const pos = getTargetPos();
    if (pos == null) return false;
    try {
      const { state, view } = editor;
      const $pos = state.doc.resolve(pos);
      const node = state.doc.nodeAt(pos) ?? $pos.nodeAfter;
      if (!node) return false;
      const tr = state.tr.setSelection(NodeSelection.create(state.doc, pos));
      view.dispatch(tr);
      view.focus();
      return true;
    } catch {
      return false;
    }
  };

  const insertBlankBelow = () => {
    const pos = getTargetPos();
    if (pos == null) return;
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;
    const end = pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(end, { type: "paragraph" })
      .run();
  };

  const insertBlankAbove = () => {
    const pos = getTargetPos();
    if (pos == null) return;
    editor
      .chain()
      .focus()
      .insertContentAt(Math.max(0, pos), { type: "paragraph" })
      .run();
  };

  const duplicateCurrent = () => {
    const pos = getTargetPos();
    if (pos == null) return;
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;
    const end = pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(end, node.toJSON())
      .run();
  };

  const deleteCurrent = () => {
    const pos = getTargetPos();
    if (pos == null) return;
    editor
      .chain()
      .focus()
      .command(({ state, tr, dispatch }) => {
        const node = state.doc.nodeAt(pos);
        if (!node) return false;
        if (dispatch) dispatch(tr.delete(pos, pos + node.nodeSize));
        return true;
      })
      .run();
  };

  const turnInto = (fn: () => void) => {
    selectTargetNode();
    fn();
  };

  // When the menu opens, lock the drag-handle in place so its computed
  // position does not drift as the user moves the mouse over the dropdown.
  // Also set a NodeSelection so the user sees the selected block and so
  // keyboard Delete / Backspace works on isolating nodes (tabs, expandable,
  // stepper, cards, callout, codeBlock, ...).
  const onMenuChange = (opened: boolean) => {
    setMenuOpened(opened);
    if (opened) {
      selectTargetNode();
      (editor.commands as any).lockDragHandle?.();
    } else {
      (editor.commands as any).unlockDragHandle?.();
    }
  };

  return (
    <DragHandle editor={editor} onNodeChange={handleNodeChange}>
      <Menu
        shadow="md"
        width={220}
        position="right-start"
        withinPortal
        opened={menuOpened}
        onChange={onMenuChange}
        closeOnItemClick
      >
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={t("Block actions")}
            onClick={(e) => {
              // Clicking the grip should select the block (visual highlight)
              // BEFORE the menu opens, so menu actions operate on that node
              // and keyboard Delete works even without opening the menu.
              e.preventDefault();
              selectTargetNode();
              setMenuOpened((v) => !v);
            }}
          >
            <IconGripVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconPlus size={14} />}
            onClick={insertBlankAbove}
          >
            {t("Add above")}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconPlus size={14} />}
            onClick={insertBlankBelow}
          >
            {t("Add below")}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCopy size={14} />}
            onClick={duplicateCurrent}
          >
            {t("Duplicate")}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Label>{t("Turn into")}</Menu.Label>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().setParagraph().run())
            }
          >
            {t("Paragraph")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run(),
              )
            }
          >
            {t("Heading 1")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run(),
              )
            }
          >
            {t("Heading 2")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run(),
              )
            }
          >
            {t("Heading 3")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().toggleBulletList().run())
            }
          >
            {t("Bullet list")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().toggleOrderedList().run())
            }
          >
            {t("Numbered list")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().toggleTaskList().run())
            }
          >
            {t("Task list")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().toggleBlockquote().run())
            }
          >
            {t("Blockquote")}
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              turnInto(() => editor.chain().focus().toggleCodeBlock().run())
            }
          >
            {t("Code")}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={deleteCurrent}
          >
            {t("Delete")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </DragHandle>
  );
}
